/**
 * ConflictResolver — field-level 3-way merge engine for offline sync.
 *
 * Strategy (per docs/expansion_plan.md §6.2):
 *  1. Compute the set of paths changed locally (base → local) and remotely
 *     (base → server) since the shared ancestor snapshot (`base`).
 *  2. Non-overlapping changes deep-merge AUTOMATICALLY — two engineers
 *     editing different sections of the same draft never see a dialog.
 *  3. Overlapping changes (same path edited concurrently with different
 *     values) are flagged as CONFLICTS. Default policy is SERVER_WINS for
 *     auto-resolution while preserving the losing local value so the UI can
 *     offer side-by-side accept/reject afterwards.
 *
 * Array semantics: arrays (appliance matrices, circuit schedules, repeater
 * rows) are treated as ATOMIC leaves. Element-wise array merging is unsafe
 * when row order is meaningful (a shifted index silently corrupts which
 * reading belongs to which appliance), so a differing array is one conflict
 * unit, not N.
 *
 * Immutability: records with `status: 'issued'` (signed compliance certs)
 * NEVER participate in mutation merges. Any required change must go through
 * an explicit amendment/revision flow — enforced here and re-checked by
 * FormService/SyncWorker before writing.
 */

export const MERGE_POLICY = {
  SERVER_WINS: 'server_wins', // default for auto-resolution
  LOCAL_WINS: 'local_wins', // e.g. user explicitly chose "keep mine"
  MANUAL: 'manual' // unresolved — surface in ConflictResolverModal
};

export const MERGE_OUTCOME = {
  CLEAN: 'clean', // no divergence from base on either side
  AUTO_MERGED: 'auto_merged', // disjoint edits combined automatically
  AUTO_MERGED_WITH_CONFLICTS: 'auto_merged_with_conflicts', // merged + losers preserved
  CONFLICT: 'conflict' // requires human decision
};

const ISSUED_STATUSES = new Set(['issued', 'issued_signed']);

/** Paths internal to the sync engine — never part of user-facing merges. */
const SYSTEM_PATHS = new Set([
  'id',
  'mutationVersion',
  'version',
  'syncStatus',
  'updatedAt',
  'client_updated_at',
  'lastAccessedAt'
]);

const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

/** Timestamp parse that tolerates ISO strings, Dates and epoch millis. */
export const toMillis = (value) => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const t = Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Deep-diff two snapshots into a flat map of `{ [path]: {from, to} }`.
 * Objects recurse; arrays & primitives are compared atomically.
 */
export function diffSnapshots(base, current, prefix = '') {
  const deltas = new Map();

  const keys = new Set([
    ...Object.keys(base ?? {}),
    ...Object.keys(current ?? {})
  ]);

  for (const key of keys) {
    if (SYSTEM_PATHS.has(key)) continue;
    const path = prefix ? `${prefix}.${key}` : key;
    const a = base ? base[key] : undefined;
    const b = current ? current[key] : undefined;

    if (isPlainObject(a) && isPlainObject(b)) {
      const nested = diffSnapshots(a, b, path);
      nested.forEach((v, k) => deltas.set(k, v));
      continue;
    }

    if (!deepEqual(a, b)) {
      deltas.set(path, { from: a, to: b });
    }
  }

  return deltas;
}

function deepEqual(a, b) {
  if (a === b) return true;
  // NaN-safe number equality & Date coercion
  if (
    (a instanceof Date || b instanceof Date) &&
    toMillis(a) === toMillis(b) &&
    a !== undefined && b !== undefined
  ) {
    return true;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => deepEqual(a[k], b[k]));
  }
  return false;
}

/** Set a possibly-nested path on a fresh object graph without mutating input. */
function setPath(target, path, value) {
  const parts = path.split('.');
  let cursor = target;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!isPlainObject(cursor[parts[i]])) cursor[parts[i]] = {};
    cursor = cursor[parts[i]];
  }
  const leaf = parts[parts.length - 1];
  if (value === undefined) {
    delete cursor[leaf];
  } else {
    cursor[leaf] = value;
  }
}

function getPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
}
void getPath; // reserved for ConflictResolverModal granular diff view (Track 3)


/**
 * Field-level 3-way merge.
 *
 * @param {Object} params
 * @param {Object} params.base   common ancestor snapshot (pre-divergence)
 * @param {Object} params.local  local device's current version
 * @param {Object} params.server server's current version
 * @param {'server_wins'|'local_wins'} [params.policy] auto-resolution for overlaps
 * @returns {{
 *   outcome: string,
 *   merged: Object,
 *   conflicts: Array<{path, baseValue, localValue, serverValue, winner}>,
 *   localChanges: number,
 *   serverChanges: number
 * }}
 */
export function threeWayMerge({
  base,
  local,
  server,
  policy = MERGE_POLICY.SERVER_WINS
}) {
  const localDelta = diffSnapshots(base ?? {}, local ?? {});
  const serverDelta = diffSnapshots(base ?? {}, server ?? {});

  const localPaths = [...localDelta.keys()];
  const serverPaths = [...serverDelta.keys()];
  const overlap = localPaths.filter((p) => serverPaths.includes(p));

  // Start from the server version; replay local-only changes over it.
  const merged = structuredClone(server ?? {});
  const conflicts = [];

  // 1. Apply non-overlapping LOCAL changes automatically.
  const overlappingSet = new Set(overlap);
  for (const path of localPaths) {
    if (!overlappingSet.has(path)) {
      setPath(merged, path, localDelta.get(path).to);
    }
  }

  // 2. Resolve overlaps per policy.
  for (const path of overlap) {
    const localVal = localDelta.get(path).to;
    const serverVal = serverDelta.get(path).to;

    // Identical concurrent edits are not real conflicts.
    if (deepEqual(localVal, serverVal)) {
      setPath(merged, path, serverVal);
      continue;
    }

    const winner =
      policy === MERGE_POLICY.LOCAL_WINS ? 'local' : 'server';

    conflicts.push({
      path,
      baseValue: localDelta.get(path).from,
      localValue: localVal,
      serverValue: serverVal,
      winner
    });

    setPath(merged, path, winner === 'local' ? localVal : serverVal);
  }

  const localChanges = localPaths.length;
  const serverChanges = serverPaths.length;

  let outcome;
  if (localChanges === 0 && serverChanges === 0) {
    outcome = MERGE_OUTCOME.CLEAN;
  } else if (conflicts.length > 0) {
    outcome = MERGE_OUTCOME.AUTO_MERGED_WITH_CONFLICTS;
  } else if (overlap.length > 0) {
    outcome = MERGE_OUTCOME.CLEAN; // identical overlapping edits
  } else {
    outcome = MERGE_OUTCOME.AUTO_MERGED;
  }

  return {
    outcome,
    merged,
    conflicts,
    localChanges,
    serverChanges
  };
}

/**
 * Decide how an inbound server record should be applied to the local copy.
 *
 * @param {Object} params
 * @param {Object} params.local       local Dexie record
 * @param {Object} params.server      inbound PocketBase record
 * @param {Object|null} params.base   last-synced ancestor snapshot (stored at push/pull time)
 * @returns {{ action: 'ignore'|'take_server'|'merge'|'conflict_review',
 *            result?: Object }} result carries merge output for 'merge'
 */
export function resolveInbound({ local, server, base }) {
  if (!local) return { action: 'take_server' };

  const issued = ISSUED_STATUSES.has(local.status);
  const localIsDirty = local.syncStatus === 'pending' || local.syncStatus === 'conflict';

  if (issued) {
    // Immutable issued certificates: server copies win wholesale, local
    // dirty state must NOT overwrite them. Any legitimate correction goes
    // through the amendment flow, producing a NEW record id.
    return { action: 'take_server' };
  }

  if (!localIsDirty) {
    // No unpushed local edits — inbound server state simply replaces cache.
    return { action: 'take_server' };
  }

  if (!base) {
    // We lost the ancestor snapshot (legacy record pre-upgrade). Fall back
    // to timestamp arbitration rather than guessing a field merge.
    // NOTE: PocketBase records carry `updated`; local rows carry `updatedAt`.
    const serverTime = Math.max(
      toMillis(server.updated),
      toMillis(server.updatedAt)
    );
    const localTime = Math.max(
      toMillis(local.updated),
      toMillis(local.updatedAt)
    );
    return serverTime > localTime
      ? { action: 'take_server' }
      : { action: 'conflict_review' };
  }

  const merge = threeWayMerge({ base: base.data ?? base, local: local.data ?? local, server: server.data ?? server });

  if (merge.conflicts.length === 0) {
    return { action: 'merge', result: merge };
  }
  return { action: 'conflict_review', result: merge };
}

/**
 * Guard for local mutations: issued certificates are locked. Callers should
 * refuse edits and route the user to the revision/amendment flow instead.
 */
export function assertMutable(record) {
  if (record && ISSUED_STATUSES.has(record.status)) {
    throw new Error(
      `Record ${record.id ?? ''} is ISSUED and immutable. Create an amendment revision instead of editing.`
    );
  }
  return true;
}

/**
 * Reconcile a pushed delta rejection: the server advanced beyond our base
 * version (optimistic-lock failure). Produces the merge needed to retry.
 */
export function handlePushRejected({ localRecord, serverRecord, baseSnapshot }) {
  const merge = threeWayMerge({
    base: baseSnapshot?.data ?? baseSnapshot ?? {},
    local: localRecord.data ?? localRecord,
    server: serverRecord.data ?? serverRecord,
    policy: MERGE_POLICY.SERVER_WINS
  });
  return merge;
}
